// ═══════════════════════════════════════════════════════
// Campaign Intelligence — Core Logic
// Ported 1:1 from CreativeIQ v2.0 (standalone HTML tool)
// All parsing, matching, metrics, and signal logic
// ═══════════════════════════════════════════════════════

import * as XLSX from 'xlsx';

// ── TYPES ──

export interface FieldDef {
  key: string;
  label: string;
  required: boolean;
  hint: string;
}

export interface ColumnMapping {
  [fieldKey: string]: string | null;
}

export interface ParsedCampaign {
  raw: string;
  group: string;
  creative: string;
  date: string;
  page: string;
  version: string;
}

export interface CampaignRow {
  campaign: string;
  account: string;
  group: string;
  creative: string;
  date: string;
  page: string;
  version: string;
  launchDate: Date | null;
  spend: number;
  clicks: number;
  cpm: number;
  cpr: number;
  lpClicks: number;
  leads: number;
  revenue: number;
  conversions: number;
  opEpc: number;
  // Computed
  profit: number;
  roi: number;
  rpl: number;
  cpulc: number;
  opCpc: number | null;
  epc: number | null;
  lpctr: number | null;
  cvr: number | null;
  signal?: SignalType;
  days?: number | null;
}

export interface GroupedRow extends CampaignRow {
  count: number;
  campaigns: CampaignRow[];
}

export type SignalType = 'win' | 'scale' | 'kill' | 'test';

export interface BuildStats {
  total: number;
  zeroSpend: number;
  noRT: number;
  noRTList: { acct: string; metaCamp: string; metaKey: string }[];
  matchedList: { acct: string; meta: string; rt: string }[];
  rtKeys: { acct: string; key: string; raw: string }[];
}

export interface AccountData {
  id: number;
  name: string;
  metaFile: string | null;
  rtFile: string | null;
  metaRaw: Record<string, any>[] | null;
  rtRaw: Record<string, any>[] | null;
  metaRows: Record<string, any>[] | null;
  rtRows: Record<string, any>[] | null;
  metaMapping: ColumnMapping | null;
  rtMapping: ColumnMapping | null;
}

export interface BuildResult {
  rows: GroupedRow[];
  stats: BuildStats;
}

// ── FIELD DEFINITIONS ──

export const META_FIELDS: FieldDef[] = [
  { key: 'campaign', label: 'Campaign Name', required: true, hint: 'The campaign name column (must follow naming convention)' },
  { key: 'account', label: 'Account Name', required: false, hint: 'Ad account / account name' },
  { key: 'spend', label: 'Amount Spent', required: true, hint: 'Spend / Cost in your currency' },
  { key: 'clicks', label: 'Unique Link Clicks', required: true, hint: 'Unique link clicks from Meta/Facebook' },
  { key: 'cpm', label: 'CPM', required: false, hint: 'Cost per 1000 impressions' },
  { key: 'cpr', label: 'Cost Per Result', required: false, hint: 'Cost per result / lead / conversion' },
  { key: 'leads', label: 'Results / Leads', required: false, hint: 'Number of results, leads, or conversions from Meta' },
  { key: 'startDate', label: 'Campaign Start Date', required: false, hint: 'Date campaign started — used to calculate days running' },
];

export const RT_FIELDS: FieldDef[] = [
  { key: 'rtcampaign', label: 'Campaign Name', required: true, hint: 'Campaign name — must match Meta campaign names' },
  { key: 'revenue', label: 'Revenue / Payout', required: true, hint: 'Total Revenue, Payout, Earnings' },
  { key: 'conversions', label: 'Conversions / Sales', required: true, hint: 'Conversions, Sales, conversion' },
  { key: 'lpClicks', label: 'LP Clicks', required: false, hint: 'Landing Page Clicks' },
  { key: 'opEpc', label: 'Offer Page EPC', required: false, hint: 'LPEPC, Offer Page EPC, EPC' },
];

// ── DEMO DATA ──

export const DEMO_META = [
  { campaign: 'CT28-CL-BROLL-RT-1-101-Noah Smith-ABO-camp1', account: 'Account Alpha', spend: 1240.50, clicks: 890, cpm: 18.40, cpr: 12.20, leads: 102, startDate: '01MAR' },
  { campaign: 'CT28-CL-BROLL-RT-1-101-Noah Smith-ABO-camp2', account: 'Account Alpha', spend: 980.20, clicks: 710, cpm: 19.10, cpr: 13.50, leads: 73 },
  { campaign: 'CT28-CL-BROLL-RT-1-101-Noah Smith-ABO-camp3', account: 'Account Alpha', spend: 1580.00, clicks: 1100, cpm: 17.90, cpr: 11.80, leads: 134 },
  { campaign: 'CT28-VID-UGC-HOOK-2-Noah Smith-CBO-camp1', account: 'Account Alpha', spend: 860.00, clicks: 540, cpm: 22.30, cpr: 16.10, leads: 54 },
  { campaign: 'CT28-VID-UGC-HOOK-2-Noah Smith-CBO-camp2', account: 'Account Alpha', spend: 1120.80, clicks: 720, cpm: 21.50, cpr: 15.20, leads: 74 },
  { campaign: 'CT28-VID-UGC-HOOK-2-Noah Smith-CBO-camp3', account: 'Account Alpha', spend: 0, clicks: 0, cpm: 0, cpr: 0, leads: 0 },
  { campaign: 'CT29-IMG-STATIC-3-Sarah Johnson-ABO-camp1', account: 'Account Beta', spend: 2100.00, clicks: 1600, cpm: 14.80, cpr: 9.50, leads: 221, startDate: '15FEB' },
  { campaign: 'CT29-IMG-STATIC-3-Sarah Johnson-ABO-camp2', account: 'Account Beta', spend: 1750.00, clicks: 1300, cpm: 15.20, cpr: 10.10, leads: 174 },
  { campaign: 'CT29-IMG-STATIC-3-Sarah Johnson-ABO-camp3', account: 'Account Beta', spend: 940.00, clicks: 680, cpm: 16.40, cpr: 11.30, leads: 83 },
  { campaign: 'CT29-CAR-DYN-5-Sarah Johnson-CBO-camp1', account: 'Account Beta', spend: 320.00, clicks: 180, cpm: 28.90, cpr: 24.50, leads: 13 },
  { campaign: 'CT30-CL-BROLL-RT-1-101-Mike Torres-ABO-camp1', account: 'Account Alpha', spend: 1890.00, clicks: 1420, cpm: 16.20, cpr: 10.40, leads: 182 },
  { campaign: 'CT30-CL-BROLL-RT-1-101-Mike Torres-ABO-camp2', account: 'Account Alpha', spend: 1340.00, clicks: 980, cpm: 17.10, cpr: 11.90, leads: 113 },
  { campaign: 'CT30-VID-UGC-HOOK-2-Mike Torres-CBO-camp1', account: 'Account Alpha', spend: 760.00, clicks: 510, cpm: 20.80, cpr: 14.70, leads: 52 },
  { campaign: 'CT28-CL-BROLL-RT-1-101-Noah Smith-CBO-camp1', account: 'Account Beta', spend: 2800.00, clicks: 2200, cpm: 13.50, cpr: 8.20, leads: 341 },
  { campaign: 'CT28-CL-BROLL-RT-1-101-Noah Smith-CBO-camp2', account: 'Account Beta', spend: 2100.00, clicks: 1650, cpm: 14.10, cpr: 8.90, leads: 236 },
];

export const DEMO_RT = [
  { campaign: 'CT28-CL-BROLL-RT-1-101-Noah Smith-ABO-camp1', revenue: 3672.00, conversions: 51, lpClicks: 712, opEpc: 6.00 },
  { campaign: 'CT28-CL-BROLL-RT-1-101-Noah Smith-ABO-camp2', revenue: 2482.00, conversions: 31, lpClicks: 568, opEpc: 5.09 },
  { campaign: 'CT28-CL-BROLL-RT-1-101-Noah Smith-ABO-camp3', revenue: 5494.00, conversions: 67, lpClicks: 880, opEpc: 7.27 },
  { campaign: 'CT28-VID-UGC-HOOK-2-Noah Smith-CBO-camp1', revenue: 1512.00, conversions: 27, lpClicks: 432, opEpc: 4.09 },
  { campaign: 'CT28-VID-UGC-HOOK-2-Noah Smith-CBO-camp2', revenue: 1924.00, conversions: 37, lpClicks: 576, opEpc: 3.89 },
  { campaign: 'CT29-IMG-STATIC-3-Sarah Johnson-ABO-camp1', revenue: 11270.00, conversions: 155, lpClicks: 1280, opEpc: 10.25 },
  { campaign: 'CT29-IMG-STATIC-3-Sarah Johnson-ABO-camp2', revenue: 8700.00, conversions: 116, lpClicks: 1040, opEpc: 9.78 },
  { campaign: 'CT29-IMG-STATIC-3-Sarah Johnson-ABO-camp3', revenue: 4232.00, conversions: 58, lpClicks: 544, opEpc: 9.10 },
  { campaign: 'CT29-CAR-DYN-5-Sarah Johnson-CBO-camp1', revenue: 208.00, conversions: 4, lpClicks: 126, opEpc: 2.31 },
  { campaign: 'CT30-CL-BROLL-RT-1-101-Mike Torres-ABO-camp1', revenue: 8190.00, conversions: 91, lpClicks: 1136, opEpc: 8.39 },
  { campaign: 'CT30-CL-BROLL-RT-1-101-Mike Torres-ABO-camp2', revenue: 5424.00, conversions: 56, lpClicks: 784, opEpc: 8.06 },
  { campaign: 'CT30-VID-UGC-HOOK-2-Mike Torres-CBO-camp1', revenue: 1300.00, conversions: 26, lpClicks: 408, opEpc: 3.71 },
  { campaign: 'CT28-CL-BROLL-RT-1-101-Noah Smith-CBO-camp1', revenue: 17732.00, conversions: 220, lpClicks: 1760, opEpc: 11.73 },
  { campaign: 'CT28-CL-BROLL-RT-1-101-Noah Smith-CBO-camp2', revenue: 12744.00, conversions: 156, lpClicks: 1320, opEpc: 11.27 },
];

// ── UTILITIES ──

function norm(s: string): string {
  return String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

export function parseNum(v: any): number {
  if (v === null || v === undefined || v === '') return 0;
  if (typeof v === 'number') return isNaN(v) ? 0 : v;
  const s = String(v).replace(/[$£€,\s]/g, '').trim();
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
}

export function normCamp(s: string): string {
  s = String(s || '').trim();
  return s.replace(/\s+/g, ' ').replace(/\u00a0/g, ' ').replace(/[–—]/g, '-').toLowerCase();
}

export function fmt$(v: number | null | undefined): string {
  if (v == null || isNaN(v)) return '—';
  return '$' + Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function fmtR(v: number | null | undefined): string {
  if (v == null || isNaN(v)) return '—';
  return (v * 100).toFixed(1) + '%';
}

export function fmtN(v: number | null | undefined): string {
  if (v == null || isNaN(v)) return '—';
  return (v * 100).toFixed(1) + '%';
}

export function fmtD(v: number | null | undefined): string {
  if (v == null || isNaN(v)) return '—';
  return '$' + Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function fmtV(v: number | null | undefined): string {
  if (v == null || isNaN(v)) return '—';
  return v.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

// ── DATE HELPERS ──

const MONTHS: Record<string, number> = {
  JAN: 0, FEB: 1, MAR: 2, APR: 3, MAY: 4, JUN: 5,
  JUL: 6, AUG: 7, SEP: 8, OCT: 9, NOV: 10, DEC: 11,
};

export function parseDateStr(s: any): Date | null {
  if (s === null || s === undefined || s === '') return null;
  if (typeof s === 'number') {
    const excelEpoch = new Date(1899, 11, 30);
    const d = new Date(excelEpoch.getTime() + s * 86400000);
    return isNaN(d.getTime()) ? null : d;
  }
  s = String(s).trim();
  if (!s) return null;
  const m = s.match(/^(\d{1,2})([A-Z]{3})(\d{2,4})?$/i);
  if (m) {
    const day = parseInt(m[1]);
    const mo = MONTHS[(m[2] || '').toUpperCase()];
    if (mo === undefined) return null;
    let yr = m[3] ? parseInt(m[3]) : new Date().getFullYear();
    if (yr < 100) yr += 2000;
    const d = new Date(yr, mo, day);
    if (d > new Date()) d.setFullYear(d.getFullYear() - 1);
    return d;
  }
  const d2 = new Date(s);
  return isNaN(d2.getTime()) ? null : d2;
}

export function daysRunning(d: Date | null): number | null {
  if (!d) return null;
  const n = Math.floor((Date.now() - d.getTime()) / 86400000);
  return n >= 0 ? n : null;
}

export function fmtDays(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  if (n === 0) return 'Today';
  if (n === 1) return '1d';
  return n + 'd';
}

// ── CAMPAIGN NAME PARSER ──

export function parseCampaign(name: string): ParsedCampaign {
  name = String(name || '').trim();
  if (!name) return { raw: name, group: '', creative: '', date: '', page: 'Unknown', version: '' };
  const parts = name.split('-');

  let pageIdx = -1;
  for (let i = 0; i < parts.length; i++) {
    if (parts[i].indexOf(' ') !== -1) { pageIdx = i; break; }
  }

  if (pageIdx === -1) {
    return { raw: name, group: parts[0] || '', creative: parts.slice(1).join('-'), date: '', page: 'Unknown', version: '' };
  }

  const group = parts[0] || '';
  let creative = parts.slice(1, pageIdx).join('-');
  const page = parts[pageIdx];
  const version = parts.slice(pageIdx + 1).join('-');

  const dateMatch = creative.match(/(?:^|-)((\d{1,2}[A-Z]{3}))$/);
  let date = '';
  if (dateMatch) {
    date = dateMatch[2];
    creative = creative.slice(0, creative.length - dateMatch[0].length);
  }

  return { raw: name, group, creative, date, page, version };
}

// ── METRICS ──

export function calcMetrics(m: any): CampaignRow {
  const profit = (m.revenue || 0) - (m.spend || 0);
  const roi = m.spend > 0 ? profit / m.spend : 0;
  const rpl = m.conversions > 0 ? m.revenue / m.conversions : 0;
  const cpulc = m.clicks > 0 ? m.spend / m.clicks : 0;
  const lp = m.lpClicks || 0;
  const opCpc = lp > 0 ? m.spend / lp : null;
  const epc = lp > 0 ? m.revenue / lp : null;
  const lpctr = (m.lpctr != null && m.lpctr > 0) ? m.lpctr : (lp > 0 && m.clicks > 0 ? lp / m.clicks : null);
  const cvr = lp > 0 ? m.conversions / lp : null;
  return { ...m, profit, roi, rpl, cpulc, opCpc, epc, lpctr, cvr };
}

export function signal(r: CampaignRow): SignalType {
  if (r.roi >= 0.5 && r.spend >= 500) return 'win';
  if (r.roi >= 0.15 && r.spend >= 300) return 'scale';
  if (r.roi < -0.1 && r.spend >= 200) return 'kill';
  return 'test';
}

// ── XLS READER ──

export function readXLS(file: File): Promise<Record<string, any>[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('FileReader failed — check file is not corrupted'));
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const wb = XLSX.read(data, { type: 'array', cellDates: true, dateNF: 'yyyy-mm-dd' });
        if (!wb.SheetNames || !wb.SheetNames.length) { reject(new Error('No sheets found in file')); return; }
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: '', raw: false });
        if (!rows || !rows.length) { reject(new Error('Sheet appears empty')); return; }
        resolve(rows as Record<string, any>[]);
      } catch (err) {
        reject(err);
      }
    };
    reader.readAsArrayBuffer(file);
  });
}

// ── COLUMN MAPPING ──

export function applyMapping(rows: Record<string, any>[], mapping: ColumnMapping): Record<string, any>[] {
  return rows.map((row) => {
    const out: Record<string, any> = {};
    Object.keys(mapping).forEach((key) => {
      const col = mapping[key];
      if (!col) return;
      out[key] = row[col];
    });
    return out;
  });
}

export function autoDetect(headers: string[], fields: FieldDef[]): ColumnMapping {
  const hints: Record<string, string[]> = {
    campaign: ['campaign name', 'campaign'],
    account: ['account name', 'account', 'ad account'],
    spend: ['amount spent', 'amount spend', 'total spent', 'total spend', 'money spent', 'budget spent', 'ad spend'],
    clicks: ['unique link clicks', 'unique clicks', 'link clicks'],
    cpm: ['cpm (cost per', 'cpm'],
    cpr: ['cost per result', 'cost per lead', 'cost per conversion', 'cost per action', 'cpl', 'cpa'],
    lpctr: ['landing page view rate', 'landing page ctr', 'lp view rate', 'lp ctr', 'landing page rate'],
    leads: ['results', 'leads', 'purchases', 'actions'],
    startDate: ['reporting starts', 'start date', 'campaign start', 'starts'],
    revenue: ['total revenue', 'revenue', 'payout', 'total payout', 'income', 'earnings'],
    conversions: ['conversion', 'conversions', 'sales', 'approved', 'conv'],
    lpClicks: ['lp clicks', 'landing page clicks', 'lander clicks', 'lp click'],
    opEpc: ['lpepc', 'offer page epc', 'offer epc', 'lp epc', 'epc'],
    rtcampaign: ['rt campaign', 'campaign name', 'campaign'],
  };

  const result: ColumnMapping = {};
  fields.forEach((f) => {
    const kws = hints[f.key] || [norm(f.label)];
    let best: string | null = null;
    headers.forEach((h) => {
      const hn = norm(h);
      kws.forEach((kw) => {
        if (!best && (hn === norm(kw) || hn.indexOf(norm(kw)) !== -1 || norm(kw).indexOf(hn) !== -1)) {
          best = h;
        }
      });
    });
    result[f.key] = best;
  });
  return result;
}

// ── DATA BUILD ──

export function buildDataset(accounts: AccountData[]): BuildResult {
  const allCampaigns: CampaignRow[] = [];
  const stats: BuildStats = { total: 0, zeroSpend: 0, noRT: 0, noRTList: [], matchedList: [], rtKeys: [] };

  accounts.forEach((acct) => {
    if (!acct.metaRows || !acct.rtRows) return;

    // Index RT
    const rtIdx: Record<string, any> = {};
    acct.rtRows.forEach((r) => {
      const rn = String(r.rtcampaign || r.campaign || '').trim();
      if (!rn || rn.indexOf('{{') !== -1) return;
      const k = normCamp(rn);
      rtIdx[k] = r;
      stats.rtKeys.push({ acct: acct.name, key: k, raw: rn });
    });

    // Aggregate Meta rows by campaign name
    const metaAgg: Record<string, any> = {};
    acct.metaRows.forEach((m) => {
      const spend = parseNum(m.spend);
      if (spend <= 0) return;
      const campName = String(m.campaign || '').trim();
      if (!campName) return;
      if (!metaAgg[campName]) {
        metaAgg[campName] = {
          campaign: campName,
          spend: 0, clicks: 0, leads: 0,
          _cpmSum: 0, _cprSum: 0, _spendForCpm: 0,
          startDate: m.startDate || null,
        };
      }
      const a = metaAgg[campName];
      a.spend += spend;
      a.clicks += parseNum(m.clicks);
      a.leads += parseNum(m.leads);
      a._cpmSum += parseNum(m.cpm) * spend;
      a._cprSum += parseNum(m.cpr) * spend;
      a._spendForCpm += spend;
      if (!a.startDate && m.startDate) a.startDate = m.startDate;
    });

    // Count zero-spend rows
    acct.metaRows.forEach((m) => {
      stats.total++;
      if (parseNum(m.spend) <= 0) stats.zeroSpend++;
    });

    // Match each unique campaign to RT
    Object.values(metaAgg).forEach((m: any) => {
      try {
        const campName = m.campaign;
        const mk = normCamp(campName);
        let rt = rtIdx[mk];

        // Fallback: alphanumeric-only match
        if (!rt) {
          const mkS = mk.replace(/[^a-z0-9]/g, '');
          let bestKey: string | null = null;
          Object.keys(rtIdx).forEach((rk) => {
            if (rk.replace(/[^a-z0-9]/g, '') === mkS) bestKey = rk;
          });
          if (bestKey) rt = rtIdx[bestKey];
        }

        if (!rt) {
          stats.noRT++;
          stats.noRTList.push({ acct: acct.name, metaCamp: campName, metaKey: mk });
        } else {
          stats.matchedList.push({ acct: acct.name, meta: campName, rt: rt.campaign || '(mapped)' });
        }

        rt = rt || {};
        const parsed = parseCampaign(campName);
        const rtLpClicks = parseNum(rt.lpClicks);
        const launchDate = parseDateStr(m.startDate) || parseDateStr(parsed.date) || null;
        const row: any = {
          campaign: campName,
          account: String(acct.name || 'Unknown'),
          group: parsed.group, creative: parsed.creative, date: parsed.date, page: parsed.page, version: parsed.version,
          launchDate,
          spend: m.spend,
          clicks: m.clicks,
          cpm: m._spendForCpm > 0 ? m._cpmSum / m._spendForCpm : 0,
          cpr: m._spendForCpm > 0 ? m._cprSum / m._spendForCpm : 0,
          lpClicks: rtLpClicks,
          leads: m.leads,
          revenue: parseNum(rt.revenue),
          conversions: parseNum(rt.conversions),
          opEpc: parseNum(rt.opEpc),
        };
        allCampaigns.push(calcMetrics(row));
      } catch (rowErr) {
        console.warn('CIQ: skipping row due to error:', rowErr, m);
      }
    });
  });

  // Group by account+creative+page
  const grpMap: Record<string, { account: string; creative: string; page: string; campaigns: CampaignRow[] }> = {};
  allCampaigns.forEach((c) => {
    const k = [c.account, c.creative, c.page].join('|||');
    if (!grpMap[k]) grpMap[k] = { account: c.account, creative: c.creative, page: c.page, campaigns: [] };
    grpMap[k].campaigns.push(c);
  });

  const rows: GroupedRow[] = Object.values(grpMap).map((g) => {
    const cs = g.campaigns;
    const sum = (f: string) => cs.reduce((s, c: any) => s + (parseFloat(c[f]) || 0), 0);
    const spendTot = sum('spend') || 1;
    const clicksTot = sum('clicks') || 1;
    const totalLpClicks = sum('lpClicks');
    const groupDate = cs.reduce<Date | null>((best, c) => {
      if (!c.launchDate) return best;
      return (!best || c.launchDate < best) ? c.launchDate : best;
    }, null);
    const agg: any = {
      account: g.account, creative: g.creative, page: g.page, count: cs.length, campaigns: cs,
      spend: sum('spend'), clicks: sum('clicks'), revenue: sum('revenue'),
      conversions: sum('conversions'), lpClicks: totalLpClicks, leads: sum('leads'),
      launchDate: groupDate,
      cpm: cs.reduce((s, c) => s + c.cpm * c.spend, 0) / spendTot,
      cpr: cs.reduce((s, c) => s + c.cpr * c.spend, 0) / spendTot,
      lpctr: clicksTot > 0 && totalLpClicks > 0 ? totalLpClicks / clicksTot : null,
    };
    const r = calcMetrics(agg) as GroupedRow;
    r.count = cs.length;
    r.campaigns = cs;
    r.signal = signal(r);
    r.days = daysRunning(r.launchDate);
    return r;
  });

  return { rows, stats };
}

// ── CSV EXPORT ──

export function exportCSV(allRows: GroupedRow[]): void {
  const hdr = ['Account', 'Creative', 'Page', '#Camps', 'Days Running', 'Launch Date', 'Spend', 'Revenue', 'Conv', 'Profit', 'ROI%', '$/Click', 'CPM', 'CPR', 'RPL', 'LPCTR%', 'OpCPC', 'OpEPC', 'CVR%', 'Signal'];
  const rows = allRows.map((r) => {
    const dr = daysRunning(r.launchDate);
    const ld = r.launchDate ? r.launchDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '';
    return [
      r.account, r.creative, r.page, r.count, dr != null ? dr : '', ld,
      r.spend.toFixed(2), r.revenue.toFixed(2), Math.round(r.conversions || 0),
      r.profit.toFixed(2), (r.roi * 100).toFixed(1), r.cpulc.toFixed(2),
      r.cpm.toFixed(2), r.cpr.toFixed(2), r.rpl.toFixed(2),
      r.lpctr != null ? (r.lpctr * 100).toFixed(2) : '',
      (r.opCpc != null ? r.opCpc : 0).toFixed(2),
      (r.epc != null ? r.epc : 0).toFixed(2),
      r.cvr != null ? (r.cvr * 100).toFixed(2) : '',
      r.signal,
    ];
  });
  const csv = [hdr, ...rows].map((r) => r.join(',')).join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  a.download = 'campaign_intelligence_export.csv';
  a.click();
}
